/** Small form controls for the inspector and the Data panel (labelled, keyboard accessible). */
import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export function Section({ title, children, defaultOpen = true, actions, testId }: { title: string; children: ReactNode; defaultOpen?: boolean; actions?: ReactNode; testId?: string }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <section className="section" aria-labelledby={`${id}-h`} data-testid={testId}>
      <div className="section-head">
        <button type="button" className="section-toggle" aria-expanded={open} aria-controls={`${id}-b`} id={`${id}-h`} onClick={() => setOpen(!open)}>
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
          <span>{title}</span>
        </button>
        {actions && <div className="section-actions">{actions}</div>}
      </div>
      {open && (
        <div className="section-body" id={`${id}-b`}>
          {children}
        </div>
      )}
    </section>
  );
}

export function Row({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`row${className ? ` ${className}` : ''}`}>{children}</div>;
}

interface NumberProps {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  placeholder?: string;
  disabled?: boolean;
  testId?: string;
  short?: string;
}

/** A number input that commits valid values as you type; Up/Down step (Shift ×10). */
export function NumberField({ label, value, onChange, min, max, step = 1, suffix, placeholder, disabled, testId, short }: NumberProps) {
  const id = useId();
  const shown = value === undefined ? '' : String(Math.round(value * 100) / 100);
  const [text, setText] = useState(shown);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(shown);
  }, [shown, focused]);
  const commit = (raw: string) => {
    setText(raw);
    if (raw.trim() === '' || raw === '-') return;
    let n = Number(raw.replace(',', '.'));
    if (!Number.isFinite(n)) return;
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    onChange(n);
  };
  return (
    <label className="field number" htmlFor={id} title={label}>
      <span className="field-label">{short ?? label}</span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        aria-label={label}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setText(shown);
        }}
        onChange={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
          e.preventDefault();
          const d = (e.key === 'ArrowUp' ? 1 : -1) * step * (e.shiftKey ? 10 : 1);
          commit(String(Math.round(((value ?? 0) + d) * 1000) / 1000));
        }}
      />
      {suffix && <span className="field-suffix">{suffix}</span>}
    </label>
  );
}

export function TextField({ label, value, onChange, multiline, testId, placeholder, maxLength, rows = 3, inputRef, onBlur }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  testId?: string;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  inputRef?: React.Ref<HTMLTextAreaElement & HTMLInputElement>;
  onBlur?: () => void;
}) {
  const id = useId();
  return (
    <label className="field text" htmlFor={id}>
      <span className="field-label">{label}</span>
      {multiline ? (
        <textarea id={id} ref={inputRef} rows={rows} value={value} placeholder={placeholder} maxLength={maxLength} data-testid={testId} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
      ) : (
        <input id={id} ref={inputRef} type="text" value={value} placeholder={placeholder} maxLength={maxLength} data-testid={testId} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
      )}
    </label>
  );
}

const HEX = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Colour as #RRGGBB or #RRGGBBAA: a swatch picker (keeps the alpha) and the hex text. */
export function ColorField({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId?: string }) {
  const id = useId();
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const alpha = value.length === 9 ? value.slice(7) : '';
  return (
    <div className="field color">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input type="color" aria-label={`${label} picker`} value={value.slice(0, 7)} onChange={(e) => onChange(`${e.target.value.toUpperCase()}${alpha}`)} />
      <input
        id={id}
        type="text"
        className="hex"
        value={text}
        spellCheck={false}
        data-testid={testId}
        onChange={(e) => {
          const v = e.target.value.trim();
          setText(v);
          const norm = v.startsWith('#') ? v : `#${v}`;
          if (HEX.test(norm)) onChange(norm.toUpperCase());
        }}
        onBlur={() => setText(value)}
      />
    </div>
  );
}

export function SelectField<T extends string>({ label, value, options, onChange, testId, disabled }: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
  testId?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <label className="field select" htmlFor={id}>
      <span className="field-label">{label}</span>
      <select id={id} value={value} disabled={disabled} data-testid={testId} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Toggle({ label, checked, onChange, testId }: { label: string; checked: boolean; onChange: (v: boolean) => void; testId?: string }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} data-testid={testId} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function IconButton({ label, onClick, children, disabled, pressed, testId, className }: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  pressed?: boolean;
  testId?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`icon-btn${pressed ? ' pressed' : ''}${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Keyboard operation of a role="tablist" (WAI-ARIA tabs pattern): ←/→ (and ↑/↓) move to the previous/next tab,
 * Home/End to the first/last, and select it (automatic activation). Put it on the tablist element.
 */
export function onTablistKeyDown(e: KeyboardEvent<HTMLElement>): void {
  const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
  if (!keys.includes(e.key) || e.altKey || e.ctrlKey || e.metaKey) return;
  const tabs = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]:not([disabled])')];
  const at = tabs.indexOf(document.activeElement as HTMLElement);
  if (at < 0 || tabs.length === 0) return;
  e.preventDefault();
  e.stopPropagation();
  const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (at + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) + tabs.length) % tabs.length;
  tabs[next]!.focus();
  tabs[next]!.click();
}

/** Props of one tab in a tablist: selected state and roving tabindex (only the selected tab is in the Tab order). */
export const tabProps = (selected: boolean, controls?: string) => ({ role: 'tab' as const, 'aria-selected': selected, tabIndex: selected ? 0 : -1, ...(controls ? { 'aria-controls': controls } : {}) });
